import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { TaskCard, TaskEmptyState } from './TaskCard'
import { useTasks } from '@/hooks/useTasks'
import { cn } from '@/utils'

interface TaskListProps {
  showAll?: boolean
}

export function TaskList({ showAll = false }: TaskListProps) {
  const { tasks, isLoading, cancelTask } = useTasks()

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <motion.div
          className="w-10 h-10 border-3 border-gold/30 border-t-gold rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
        <p className="mt-4 text-sm text-white/50">Loading tasks...</p>
      </div>
    )
  }

  // Filter tasks based on showAll prop
  const activeTasks = tasks.filter((t) => t.status === 'running' || t.status === 'pending')

  if (!showAll && activeTasks.length === 0) {
    return <TaskEmptyState />
  }

  if (showAll && tasks.length === 0) {
    return <TaskEmptyState />
  }

  // Group tasks by status
  const runningTasks = tasks.filter((t) => t.status === 'running')
  const pendingTasks = tasks.filter((t) => t.status === 'pending')
  const completedTasks = tasks.filter((t) => t.status === 'completed')
  const failedTasks = tasks.filter((t) => t.status === 'failed')
  const cancelledTasks = tasks.filter((t) => t.status === 'cancelled')

  if (!showAll) {
    // Simple list for sidebar
    return (
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {activeTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onCancel={
                task.status === 'running' || task.status === 'pending'
                  ? () => cancelTask(task.id)
                  : undefined
              }
            />
          ))}
        </AnimatePresence>
      </div>
    )
  }

  // Full grouped list for tasks page
  return (
    <div className="space-y-8">
      {/* Running Tasks */}
      {runningTasks.length > 0 && (
        <TaskGroup
          title="Running"
          count={runningTasks.length}
          tasks={runningTasks}
          onCancel={cancelTask}
          accentColor="blue"
        />
      )}

      {/* Pending Tasks */}
      {pendingTasks.length > 0 && (
        <TaskGroup
          title="Pending"
          count={pendingTasks.length}
          tasks={pendingTasks}
          onCancel={cancelTask}
          accentColor="warning"
        />
      )}

      {/* Failed Tasks */}
      {failedTasks.length > 0 && (
        <TaskGroup
          title="Failed"
          count={failedTasks.length}
          tasks={failedTasks}
          accentColor="danger"
        />
      )}

      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <TaskGroup
          title="Completed"
          count={completedTasks.length}
          tasks={completedTasks}
          defaultCollapsed
          accentColor="success"
        />
      )}

      {/* Cancelled Tasks */}
      {cancelledTasks.length > 0 && (
        <TaskGroup
          title="Cancelled"
          count={cancelledTasks.length}
          tasks={cancelledTasks}
          defaultCollapsed
          accentColor="gray"
        />
      )}
    </div>
  )
}

interface TaskGroupProps {
  title: string
  count: number
  tasks: ReturnType<typeof useTasks>['tasks']
  onCancel?: (taskId: string) => void
  defaultCollapsed?: boolean
  accentColor: 'blue' | 'warning' | 'success' | 'danger' | 'gray'
}

function TaskGroup({ title, count, tasks, onCancel, defaultCollapsed = false, accentColor }: TaskGroupProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  const colorClasses = {
    blue: 'bg-blue/20 text-blue-light',
    warning: 'bg-warning/20 text-warning',
    success: 'bg-success/20 text-success',
    danger: 'bg-danger/20 text-danger',
    gray: 'bg-white/10 text-white/50',
  }

  return (
    <div>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between mb-4 group"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-white/70 group-hover:text-white transition-colors">
            {title}
          </h3>
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', colorClasses[accentColor])}>
            {count}
          </span>
        </div>
        <motion.div
          animate={{ rotate: collapsed ? 0 : 180 }}
          className="text-white/40"
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </button>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3 overflow-hidden"
          >
            <AnimatePresence mode="popLayout">
              {tasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onCancel={onCancel ? () => onCancel(task.id) : undefined}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
